import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import Header from "src/components/app/layout/Header";
import Button from "src/components/app/Button";
import TextArea from "src/components/app/TextArea";
import Radio from "src/components/common/Radio";
import { useAppSelector, useAppDispatch } from "src/redux/hooks";
import * as patientActions from "src/redux/slices/patientSlice";
import Layout from "src/components/app/layout/Layout";
import type { QueryReason } from "src/types/patient";
import { CheckCircle2 } from "lucide-react";

type HelpOption =
  | "purchasing_new_cgm"
  | "connecting_my_cgm"
  | "having_troubles_after_connection"
  | "other";

export default function CGMNeedHelpScreen() {
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";

  const { patientQueryList: queryList } = useAppSelector(
    (state) => state.patient
  );

  const [selectedQueryReason, setSelectedQueryReason] =
    useState<QueryReason | null>(null);
  const [otherText, setOtherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    getQueryList();
  }, [dispatch]);

  const getQueryList = async () => {
    new Promise((resolve, reject) => {
      dispatch(
        patientActions.getPatientQueryListRequest({
          resolve,
          reject,
          payload: { queryType: "CGM" },
        })
      );
    });
  };

  const handleOptionSelect = (option: HelpOption) => {
    // Find the corresponding QueryReason from the API response
    const queryReason = queryList?.data?.find(
      (item: QueryReason) =>
        item?.query_reason?.toLowerCase().replace(/\s+/g, "_") === option
    );

    setSelectedQueryReason(queryReason || null);
    if (option !== "other") {
      setOtherText("");
    }
  };

  const handleProceed = async () => {
    if (!selectedQueryReason || isSubmitting) return;

    const isOtherOption =
      selectedQueryReason?.query_reason?.toLowerCase() === "other";

    // Prepare payload
    const description = isOtherOption
      ? otherText.trim()
      : selectedQueryReason?.query_reason;

    const payload = {
      description,
      query_reason_master_id: selectedQueryReason?.query_reason_master_id,
    };

    setIsSubmitting(true);

    new Promise((resolve, reject) => {
      dispatch(
        patientActions.sendPatientQueryRequest({
          payload,
          resolve,
          reject,
        })
      );
    })
      .then(() => {
        setSuccess(true);
      })
      .catch((error) => {
        toast.error(
          error instanceof Error ? error.message : "Failed to submit your query"
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const isProceedEnabled =
    selectedQueryReason !== null &&
    (selectedQueryReason?.query_reason?.toLowerCase() !== "other" ||
      otherText?.trim().length > 0);

  // Map API response to options format
  const options: { value: HelpOption; label: string }[] =
    (queryList?.data || [])?.map((item: QueryReason) => ({
      value: item?.query_reason
        ?.toLowerCase()
        .replace(/\s+/g, "_") as HelpOption,
      label: item?.query_reason,
    })) || [];

  // Get the selected option value for the OptionSelectorWithTextarea component
  const selectedOption = selectedQueryReason
    ? (selectedQueryReason?.query_reason
        ?.toLowerCase()
        .replace(/\s+/g, "_") as HelpOption)
    : null;

  if (success) {
    return (
      <Layout
        supportProgramKey={supportProgramKey}
        enableTheme={true}
        header={
          <Header
            title="Back"
            showBackButton={true}
            className="bg-white"
            onBackClick={() => navigate(-1)}
          />
        }
      >
        <div className="min-h-full bg-white px-4 py-10 flex flex-col items-center justify-center text-center">
          <CheckCircle2 className="h-16 w-16 text-green-600" />
          <h1 className="mt-6 text-2xl font-semibold font-playfair text-text-primary">
            Request sent
          </h1>
          <p className="mt-3 max-w-sm text-sm text-gray-500">
            Our CGM support team has received your request and will get in touch
            with you soon.
          </p>
          <div className="mt-8 w-full max-w-sm">
            <Button fullWidth onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      supportProgramKey={supportProgramKey}
      enableTheme={true}
      header={
        <Header
          title="Back"
          showBackButton={true}
          className="bg-white"
          onBackClick={() => navigate(-1)}
        />
      }
      footer={
        <div className="bg-white px-3 py-4 border-t border-gray-200">
          <Button
            fullWidth
            disabled={!isProceedEnabled || isSubmitting}
            onClick={handleProceed}
            loading={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Proceed"}
          </Button>
        </div>
      }
    >
      <div className="px-4 pt-6 pb-4">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-text-primary mb-3 font-playfair">
            Connect with our experts
          </h1>
          <p className="text-sm text-gray-500">
            We are here to help. Let us know where you are facing any difficulty
            and chat with our expert team who will guide you in this journey
          </p>
        </div>

        <p className="text-lg text-black mb-4 font-semibold">
          What do you want help with?
        </p>

        <div className="flex flex-col gap-4">
          <ul className="flex flex-col gap-4">
            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  onClick={() => handleOptionSelect(opt.value as any)}
                  className={`flex items-center gap-2 w-full text-left bg-white border rounded-lg px-4 py-3 ${
                    selectedOption === opt.value
                      ? "border-[var(--app-primary)] bg-[var(--app-primary-bg)]"
                      : "border-gray-300"
                  }`}
                >
                  <Radio
                    checked={selectedOption === opt.value}
                    onChange={() => handleOptionSelect(opt.value as any)}
                  />
                  <p className="font-medium text-[var(--text-primary)]">
                    {opt.label}
                  </p>
                </button>
              </li>
            ))}
          </ul>

          {selectedOption?.toLowerCase() === "other" && (
            <TextArea
              name="other_text"
              type="text"
              placeholder="What issues are you facing?"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              fullWidth={true}
              rows={4}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
